import React, { useEffect, useState } from "react";
import {
  ArrowRightIcon,
  CheckCircleIcon,
  ClockIcon,
  CreditCardIcon,
} from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { userApi } from "../../../repositories/user";
import { transactionApi } from "../../../repositories/transaction";
import { handleError } from "../../../utils/errorHandler";

interface PurchaseData {
  id: number;
  email: string;
  productId: string;
  status: string;
  transactionId: string;
  pitchLimit: number;
  userId: number;
  packageName: string;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

interface Price {
  id: string;
  object: string;
  active: boolean;
  billing_scheme: string;
  created: number;
  currency: string;
  custom_unit_amount: number | null;
  livemode: boolean;
  lookup_key: string | null;
  metadata: { [key: string]: string };
  nickname: string | null;
  product: string;
  recurring: any | null;
  tax_behavior: string;
  tiers_mode: string | null;
  transform_quantity: any | null;
  type: string;
  unit_amount: number;
  unit_amount_decimal: string;
}

interface StripeProduct {
  id: string;
  name: string;
  description: string;
  active: boolean;
  metadata: {
    payment_link: string;
    pitch_limit: string;
    product_id: string;
  };
  prices: Price[];
}

interface ProfileData {
  purchase: PurchaseData | null;
}

// Skeleton component for loading state
const SkeletonLoader = () => {
  return (
    <div className="max-w-4xl p-5 bg-white shadow-md rounded-xl">
      {/* Header skeleton */}
      <div className="flex items-center justify-between mb-6">
        <div className="w-40 h-8 bg-gray-200 rounded-md animate-pulse"></div>
        <div className="w-16 h-6 bg-gray-200 rounded-full animate-pulse"></div>
      </div>

      {/* Current Plan Overview skeleton */}
      <div className="p-6 mb-8 bg-gray-100 rounded-xl">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div className="mb-4 md:mb-0">
            <div className="w-32 h-6 mb-2 bg-gray-200 rounded-md animate-pulse"></div>
            <div className="w-40 h-4 bg-gray-200 rounded-md animate-pulse"></div>
          </div>
          <div className="h-5 bg-gray-200 rounded-md w-36 animate-pulse"></div>
        </div>
      </div>

      {/* Credit Usage and Expiration skeleton */}
      <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2">
        <div className="p-6 bg-white border border-gray-200 rounded-xl">
          <div className="w-32 h-4 mb-3 bg-gray-200 rounded-md animate-pulse"></div>
          <div className="w-20 h-8 mb-4 bg-gray-200 rounded-md animate-pulse"></div>
        </div>

        <div className="p-6 bg-white border border-gray-200 rounded-xl">
          <div className="w-32 h-4 mb-3 bg-gray-200 rounded-md animate-pulse"></div>
          <div className="w-24 h-8 mb-4 bg-gray-200 rounded-md animate-pulse"></div>
        </div>
      </div>

      {/* Button skeleton */}
      <div className="w-full h-12 mb-8 bg-gray-200 rounded-lg animate-pulse"></div>

      {/* Available Plans skeleton */}
      <div>
        <div className="w-40 mb-6 bg-gray-200 rounded-md h-7 animate-pulse"></div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((item) => (
            <div
              key={item}
              className="p-6 bg-white border border-gray-200 rounded-xl"
            >
              <div className="w-24 h-6 mb-3 bg-gray-200 rounded-md animate-pulse"></div>
              <div className="w-20 h-8 mb-3 bg-gray-200 rounded-md animate-pulse"></div>
              <div className="w-full h-4 mb-6 bg-gray-200 rounded-md animate-pulse"></div>
              <div className="w-full h-10 mt-4 bg-gray-200 rounded-md animate-pulse"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PitchPack: React.FC = () => {
  const [profileData, setProfileData] = useState<ProfileData>({
    purchase: null,
  });
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch user profile data
        const profileResponse = await userApi.getProfile();
        if (!profileResponse?.data?.success) {
          throw new Error(
            profileResponse?.data?.error || "Failed to fetch profile"
          );
        }
        setProfileData({
          purchase: profileResponse.data.data.purchase || null,
        });

        // Fetch available Stripe products
        const productsResponse = await transactionApi.getAllStripeProducts();
        if (!productsResponse?.data?.success) {
          throw new Error(
            productsResponse?.data?.error || "Failed to fetch products"
          );
        }
        setProducts(productsResponse.data.data || []);
      } catch (error) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handlePurchaseMore = () => {
    // Scroll to available plans section
    document
      .getElementById("available-plans")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSelectPlan = (
    paymentLink: string,
    planId: string,
    planName: string
  ) => {
    setSelectedPlan(planId);

    if (paymentLink) {
      // Redirect to the Stripe payment link
      window.location.href = paymentLink.trim();
    } else {
      alert(`No payment link available for ${planName} plan.`);
    }
  };

  // Format price from unit_amount (in cents) and currency
  const formatPrice = (unitAmount: number, currency: string): string => {
    const amountInDollars = (unitAmount / 100).toFixed(2); // Convert cents to dollars
    return currency.toUpperCase() === "USD"
      ? `$${amountInDollars}`
      : `${amountInDollars} ${currency.toUpperCase()}`;
  };

  // Calculate days remaining until expiration
  const getDaysRemaining = (expiryDate: string): number => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return <SkeletonLoader />;
  }

  const daysRemaining = profileData.purchase?.expiresAt
    ? getDaysRemaining(profileData.purchase.expiresAt)
    : 0;

  return (
    <div className="max-w-4xl p-5 bg-white shadow-md rounded-xl">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Your Pitch Pack</h2>
        {profileData.purchase && (
          <span className="px-3 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full">
            Active
          </span>
        )}
      </div>

      {/* Current Plan Overview */}
      <div className="p-6 mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div className="mb-4 md:mb-0">
            <h3 className="text-lg font-medium text-gray-700">
              {profileData.purchase?.packageName || "No Active Plan"}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Purchased:{" "}
              {profileData.purchase?.createdAt
                ? format(
                    new Date(profileData.purchase.createdAt),
                    "MMMM d, yyyy"
                  )
                : "N/A"}
            </p>
          </div>

          {profileData.purchase && (
            <div className="flex items-center">
              <ClockIcon className="w-5 h-5 mr-2 text-blue-500" />
              <span className="text-sm font-medium text-gray-700">
                {daysRemaining > 0
                  ? `Expires in ${daysRemaining} days`
                  : "Expired"}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Credit Usage and Expiration */}
      <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2">
        {/* Remaining Credits */}
        <div className="p-6 transition-all duration-200 bg-white border border-gray-200 rounded-xl hover:shadow-md">
          <h4 className="mb-1 text-sm font-medium text-gray-500">
            Remaining Credits
          </h4>
          <div className="flex items-end">
            <span className="text-3xl font-bold text-gray-800">
              {profileData.purchase?.pitchLimit || 0}
            </span>
            <span className="ml-2 text-sm text-gray-500">pitches</span>
          </div>

          {/* Progress bar removed as requested */}
        </div>

        {/* Expiration Date */}
        <div className="p-6 transition-all duration-200 bg-white border border-gray-200 rounded-xl hover:shadow-md">
          <h4 className="mb-1 text-sm font-medium text-gray-500">
            Expiration Date
          </h4>
          <div className="flex items-end">
            <span className="text-3xl font-bold text-gray-800">
              {profileData.purchase?.expiresAt
                ? format(new Date(profileData.purchase.expiresAt), "MMM d")
                : "N/A"}
            </span>
            <span className="ml-2 text-sm text-gray-500">
              {profileData.purchase?.expiresAt
                ? format(new Date(profileData.purchase.expiresAt), "yyyy")
                : ""}
            </span>
          </div>

          {profileData.purchase?.expiresAt && (
            <div className="flex items-center mt-4">
              <div
                className={`w-2 h-2 mr-2 rounded-full ${
                  daysRemaining > 30
                    ? "bg-green-500"
                    : daysRemaining > 7
                    ? "bg-yellow-500"
                    : "bg-red-500"
                }`}
              ></div>
              <p className="text-xs text-gray-500">
                {daysRemaining > 30
                  ? "Your plan is active for more than a month"
                  : daysRemaining > 7
                  ? `Your plan will expire soon (${daysRemaining} days remaining)`
                  : `Your plan is about to expire (${daysRemaining} days remaining)`}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Purchase More Credits Button */}
      <button
        onClick={handlePurchaseMore}
        className="w-full py-3 mb-8 text-base font-medium text-white transition-all duration-200 bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-200"
      >
        <div className="flex items-center justify-center">
          <CreditCardIcon className="w-5 h-5 mr-2" />
          Purchase More Credits
        </div>
      </button>

      {/* Available Plans */}
      <div id="available-plans">
        <h3 className="mb-6 text-xl font-semibold text-gray-800">
          Available Plans
        </h3>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {products.length > 0 ? (
            products
              .filter((product) => product.active)
              .map((product) => {
                const firstPrice =
                  product.prices && product.prices.length > 0
                    ? product.prices[0]
                    : null;
                const isCurrentPlan =
                  profileData.purchase?.packageName === product.name;

                return (
                  <div
                    key={product.id}
                    className={`relative overflow-hidden transition-all duration-200 bg-white border rounded-xl ${
                      isCurrentPlan
                        ? "border-blue-500 ring-2 ring-blue-200"
                        : "border-gray-200 hover:border-blue-300 hover:shadow-md"
                    }`}
                  >
                    {isCurrentPlan && (
                      <div className="absolute top-0 right-0 px-2 py-1 text-xs font-semibold text-white bg-blue-500">
                        Current Plan
                      </div>
                    )}

                    <div className="p-6">
                      <h4 className="mb-2 text-lg font-semibold text-gray-800">
                        {product.name}
                      </h4>

                      <div className="flex items-baseline mb-4">
                        <span className="text-2xl font-bold text-gray-900">
                          {firstPrice?.unit_amount && firstPrice?.currency
                            ? formatPrice(
                                firstPrice.unit_amount,
                                firstPrice.currency
                              )
                            : "Price not available"}
                        </span>
                      </div>

                      <p className="mb-6 text-sm text-gray-600">
                        {product.description}
                      </p>

                      <ul className="mb-6 space-y-2">
                        <li className="flex items-start">
                          <CheckCircleIcon className="w-5 h-5 mr-2 text-green-500 shrink-0" />
                          <span className="text-sm text-gray-600">
                            {product.metadata.pitch_limit} pitches
                          </span>
                        </li>
                      </ul>

                      <button
                        onClick={() =>
                          handleSelectPlan(
                            product.metadata.payment_link,
                            product.id,
                            product.name
                          )
                        }
                        disabled={selectedPlan === product.id}
                        className={`w-full py-2 text-sm font-medium transition-colors duration-200 rounded-lg ${
                          isCurrentPlan
                            ? "text-blue-600 bg-blue-50"
                            : selectedPlan === product.id
                            ? "text-gray-400 bg-gray-100 cursor-wait"
                            : "text-white bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:ring-blue-200"
                        }`}
                      >
                        {isCurrentPlan
                          ? "Buy Again"
                          : selectedPlan === product.id
                          ? "Processing..."
                          : "Buy now"}
                      </button>
                    </div>
                  </div>
                );
              })
          ) : (
            <div className="p-8 text-center col-span-full bg-gray-50 rounded-xl">
              <p className="text-gray-500">No available plans at the moment</p>
            </div>
          )}
        </div>
      </div>

      {/* Help text */}
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-500">
          Need help with your subscription?{" "}
          <a href="#" className="text-blue-600 hover:underline">
            Contact support
          </a>
        </p>
      </div>
    </div>
  );
};

export default PitchPack;
